import React, { Component, ReactNode } from 'react';
import { Layout, Row, Col, Menu, Dropdown, Breadcrumb } from 'antd';
import { HomeFilled } from '@ant-design/icons';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import cx from 'classnames';

import { userLogout } from '../../../slices/auth/login.thunks';
import { selectors } from '../../../slices/auth';
import Avatar from '../avatar';
import { RootState } from '../../../slices/rootReducer';
import { IUser } from '../../../slices/users/user';
import sightXIcon from '../../../assets/sightXicon.png';
import { getDefaultCoreUrl } from '../../auth/utils/getUrlToRedirect';
import { isIncrowdClient } from '../../../utils/clients';
import { FeatureToggle, Off, On } from '../../../featureToggles';
import { Features } from '../../../featureToggles/features';
import { LeftNavBar } from '../leftMenu/leftMenu';
import { truncate } from '../utils/string';

import styles from './appWrapper.module.scss';

const { Header } = Layout;

interface DispatchProps {
  goRoute: (path: string) => void;
  userLogout: () => void;
}

interface StateProps {
  user: IUser | null;
  originalUser: IUser | null;
  token: string;
  webClientId: string | null;
}

interface IAppWrapperProps {
  children: ReactNode;
}

class AppWrapper extends Component<IAppWrapperProps & DispatchProps & StateProps> {
  constructor(props: IAppWrapperProps & DispatchProps & StateProps) {
    super(props);
    this.goToHome = this.goToHome.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
  }

  private goToHome() {
    const { goRoute } = this.props;
    goRoute('/');
  }

  private async handleLogout() {
    await this.props.userLogout();
  }

  private getAvatarMenu() {
    const { user, token, webClientId = '' } = this.props;
    const userName = user ? `${user.firstName} ${user.lastName}`.trim() : '';
    const clientIdIfIncrowd = isIncrowdClient() ? `&sx_client_id=${webClientId}` : '';
    const queryObjects = `?token=${token}&userId=${user?.id}${clientIdIfIncrowd}`;
    const coreUrl = getDefaultCoreUrl(user?.account);

    return (
      <Menu className={styles.profileMenuWrapper}>
        <Menu.Item key="0" disabled>
          {truncate(userName, 20)}
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item key="1">
          <a href={`${coreUrl}${queryObjects}`}>Projects</a>
        </Menu.Item>
        <Menu.Item key="3">
          <a href={`${coreUrl}/settings${queryObjects}`}>Settings</a>
        </Menu.Item>
        <Menu.Item key="4" onClick={this.handleLogout}>
          Logout
        </Menu.Item>
      </Menu>
    );
  }

  private renderAvatar() {
    return (
      <Col sm={2} xs={2} className={styles.avatarContainer}>
        <Dropdown overlay={this.getAvatarMenu()} trigger={['click']}>
          <div className={styles.rightSide}>
            <Avatar />
          </div>
        </Dropdown>
      </Col>
    );
  }

  private renderLogo() {
    const { user, token } = this.props;
    const coreURL = `${getDefaultCoreUrl(user?.account)}?token=${token}&userId=${user?.id}`;
    return (
      <Col sm={1} xs={3}>
        <a className={styles.logo} href={coreURL}>
          <img src={sightXIcon} alt="SightX" />
        </a>
      </Col>
    );
  }

  private renderLeftMenu() {
    // TODO: verify webClientId once uirefresh is promoted to productions
    const { user, webClientId = '' } = this.props;
    const clientIdIfIncrowd = isIncrowdClient() ? `&sx_client_id=${webClientId}` : '';
    const queryObjects = `?${clientIdIfIncrowd}`;
    const coreUrl = getDefaultCoreUrl(user?.account);
    return (
      <LeftNavBar
        user={user}
        coreUrl={coreUrl}
        queryObjects={queryObjects}
        onClickLogout={this.handleLogout}
      />
    );
  }

  private renderMenu() {
    const { user } = this.props;
    const coreURL = `${getDefaultCoreUrl(user?.account)}`;
    return (
      <Col sm={21} xs={18}>
        <Breadcrumb className={styles.headerBreadcrumb}>
          <Breadcrumb.Item href={coreURL}>
            <HomeFilled className={styles.homeIcon} />
          </Breadcrumb.Item>
          <Breadcrumb.Item onClick={this.goToHome} className={styles.headerTitle}>
            Admin Dashboard
          </Breadcrumb.Item>
        </Breadcrumb>
      </Col>
    );
  }

  renderContent() {
    return this.props.children;
  }

  render(): ReactNode {
    return (
      <FeatureToggle feature={Features.Uirefresh}>
        <Off>
          <Layout className={styles.appContainerForDevelopment}>
            <Header>
              <Row>
                {this.renderLogo()}
                {this.renderMenu()}
                {this.renderAvatar()}
              </Row>
            </Header>
            {this.renderContent()}
          </Layout>
        </Off>
        <On>
          <Layout className={cx(styles.appContainerForDevelopment, styles[Features.Uirefresh])}>
            {this.renderLeftMenu()}
            {this.renderContent()}
          </Layout>
        </On>
      </FeatureToggle>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  user: selectors.getLoggedInUser(state),
  originalUser: selectors.getOriginalUser(state),
  token: selectors.getAuthToken(state),
  webClientId: selectors.getWebClientId(state)
});

export default connect(mapStateToProps, {
  goRoute: push,
  userLogout
})(AppWrapper);
